// 일시정지 장착 화면과 상점이 함께 쓰는 등급 표현 정본.
// 규칙값은 만들지 않고, gear의 네 슬롯을 실제 아이콘 + 같은 점/다이아 문법으로 보인다.
import { SLOTS, SLOT_KO, TIER_MAX, isDiamondTier } from './gear.js';
import { gearArtKey } from './gearart.js';
import { setRequirementStatus } from './gearstats.js';

// 일반 5색 뒤의 여섯 번째는 뽑기 색이 아니라 재구매 전용 다이아 광택이다.
export const TIER_COLOR = Object.freeze([
  0xf1e6cf, 0x6fa4c1, 0xd89452, 0x9a77b8, 0xc85f57, 0xbff4ff,
]);
export const TIER_CSS = Object.freeze([
  '#f1e6cf', '#6fa4c1', '#d89452', '#9a77b8', '#c85f57', '#c9f8ff',
]);

const tierIndex = (t) => Math.max(0, Math.min(TIER_COLOR.length - 1, (t | 0) - 1));
export const tierColor = (t) => TIER_COLOR[tierIndex(t)];
export const tierCss = (t) => TIER_CSS[tierIndex(t)];

// ◆는 점 여섯 개가 아니다. 다섯 점이 쓰던 폭의 한가운데에 보석 한 점만 놓는다.
export function drawTierDiamond(g, x, y, scale = 1, alpha = 1) {
  const rx = 9 * scale, ry = 8 * scale;
  g.fillStyle(0x58afc1, 0.82 * alpha);
  g.fillTriangle(x, y - ry, x, y + ry, x - rx, y);
  g.fillStyle(0xd9fbff, alpha);
  g.fillTriangle(x, y - ry, x + rx, y, x, y + ry);
  g.fillStyle(0xffffff, 0.88 * alpha);
  g.fillTriangle(x, y - ry, x + rx * 0.46, y, x, y - ry * 0.10);
  g.lineStyle(Math.max(1, 1.5 * scale), 0xf4ffff, 0.92 * alpha);
  g.lineBetween(x - rx, y, x, y - ry);
  g.lineBetween(x, y - ry, x + rx, y);
}

/** 색 외에도 점의 정확한 수/다이아 모양을 남기는 접근성용 등급 표지. */
export function tierMarker(t) {
  if (isDiamondTier(t)) return { kind: 'diamond', filled: 0, total: TIER_MAX };
  return {
    kind: 'pips',
    filled: Math.max(0, Math.min(TIER_MAX, t | 0)),
    total: TIER_MAX,
  };
}

export function drawTierDots(g, x, y, t, {
  gap = 17,
  radius = 5,
  empty = 0x3c3d3b,
  diamondScale = 1,
} = {}) {
  const marker = tierMarker(t);
  if (marker.kind === 'diamond') {
    drawTierDiamond(g, x + ((TIER_MAX - 1) * gap) / 2, y, diamondScale);
    return;
  }
  const color = tierColor(t);
  for (let i = 0; i < marker.total; i++) {
    g.fillStyle(i < marker.filled ? color : empty, 1);
    g.fillCircle(x + i * gap, y, radius);
  }
}

// 전투 왼쪽 상태판의 compact HUD 공통 자. scene도 같은 값을 읽는다.
export const COMPACT_STATUS_HUD = Object.freeze({
  x: 14,
  y: 9,
  w: 450,
  h: 78,
  radius: 12,
});

// 1920×1080 내부 좌표. 전투 위 상시 HUD가 아니라 ESC/Menu/Options 일시정지 패널
// 한가운데에서만 쓰는 현재 장착 4칸이다. 1280×720에서도 아이콘과 등급점을 읽을 수
// 있도록 상단의 구 68×126 칸보다 넓고 크게 잡았다.
export const PAUSE_GEAR_HUD = Object.freeze({
  x: 672,
  y: 370,
  cellW: 132,
  cellH: 160,
  gap: 16,
  radius: 8,
  icon: 80,
  iconTop: 32,
  setLineY: 119,
  setTextY: 178,
  setFontSize: 18,
  setTextLineH: 24,
  setTextMaxW: 576,
  pipY: 142,
  pipGap: 16,
  pipRadius: 5,
  depth: 263,
});

// 일시정지의 한 줄은 gearui.setLine과 같은 짧은 문법만 쓴다. 판정·수식어·현재
// 개수는 모두 setRequirementStatus가 준 필드이고, 발동명은 정본 문구를 그대로 둔다.
export function pauseGearHudSetCopy(status) {
  if (!status?.short) return '';
  if (status.active) return status.short;
  const count = status.count;
  if (!count?.ko || (count.n ?? 0) < 2) return '';
  if (status.kind === 'parts') return `${count.ko} ${count.n}/4 · ${4 - count.n}칸 부족`;
  if (status.kind === 'low') return `${count.ko} 4/4 · 등급 부족`;
  return '';
}

// 세트 판정과 문구는 gearstats의 정본을 그대로 읽는다. HUD는 새 규칙이나 축약 문구를
// 만들지 않고, 실제로 같은 수식어인 칸과 발동 여부만 중립 결속선으로 번역한다.
export function pauseGearHudSetState(gear) {
  const status = setRequirementStatus(gear);
  const text = pauseGearHudSetCopy(status);
  const visible = !!text && (status.active || (status.count?.n ?? 0) >= 2);
  if (!visible) {
    return { visible: false, text: '', status, slots: [], lineCount: 0,
      lineWidth: 0, lineAlpha: 0 };
  }
  const setId = status.active ? status.set?.id : status.count?.id;
  return {
    visible: true,
    text,
    status,
    slots: SLOTS.filter((slot) => gear?.[slot]?.p?.setId === setId),
    // ◆ 마커까지 침범하지 않도록 선은 언제나 하나다. 발동은 같은 중립색의 굵기와
    // 알파만 올리며, 세트 종류를 새 색으로 부호화하지 않는다.
    lineCount: 1,
    lineWidth: status.active ? 2 : 1,
    lineAlpha: status.active ? 0.92 : 0.58,
  };
}

export function pauseGearHudEntry(gear, slot) {
  const worn = gear?.[slot] ?? null;
  const id = worn?.p?.id ?? null;
  const tier = worn?.t ?? 1;
  return {
    slot,
    label: SLOT_KO[slot] ?? slot,
    id,
    tier,
    textureKey: id ? gearArtKey(slot, id) : null,
    color: tierColor(tier),
    marker: tierMarker(tier),
  };
}

export function pauseGearHudEntries(gear) {
  return SLOTS.map((slot) => pauseGearHudEntry(gear, slot));
}

export function pauseGearHudSignature(gear) {
  return SLOTS.map((slot) => {
    const worn = gear?.[slot];
    return `${slot}:${worn?.p?.id ?? '-'}:${worn?.t ?? 0}`;
  }).join('|');
}

export function pauseGearHudVisible({ shopOpen, escPaused, finale }) {
  return !!escPaused && !shopOpen && !finale;
}

export class PauseGearHud {
  constructor(scene) {
    this.scene = scene;
    this.signature = null;
    this.visible = false;
    this.g = scene.add.graphics()
      .setDepth(PAUSE_GEAR_HUD.depth).setScrollFactor(0).setVisible(false);
    this.icons = [];
    this.labels = [];
    this.setState = { visible: false, text: '', slots: [], lineCount: 0 };
    this.forceRedraw = false;

    for (let i = 0; i < SLOTS.length; i++) {
      const x = this.cellX(i);
      this.icons.push(scene.add.image(0, 0, '__DEFAULT')
        .setOrigin(0.5).setDepth(PAUSE_GEAR_HUD.depth + 1)
        .setScrollFactor(0).setVisible(false));
      this.labels.push(scene.add.text(x + PAUSE_GEAR_HUD.cellW / 2,
        PAUSE_GEAR_HUD.y + 7, SLOT_KO[SLOTS[i]], {
          fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif',
          fontSize: '17px', fontStyle: 'bold', color: '#d6c4a3',
          shadow: { offsetX: 0, offsetY: 1, color: '#000000', blur: 3, fill: true },
        }).setOrigin(0.5, 0).setDepth(PAUSE_GEAR_HUD.depth + 2)
        .setScrollFactor(0).setVisible(false));
    }
    this.setLabel = scene.add.text(
      PAUSE_GEAR_HUD.x
        + (SLOTS.length * PAUSE_GEAR_HUD.cellW
          + (SLOTS.length - 1) * PAUSE_GEAR_HUD.gap) / 2,
      PAUSE_GEAR_HUD.y + PAUSE_GEAR_HUD.setTextY,
      '', {
        fontFamily: '"Noto Sans KR Variable", "Noto Sans KR", "Malgun Gothic", system-ui, sans-serif',
        fontSize: `${PAUSE_GEAR_HUD.setFontSize}px`, fontStyle: 'bold',
        color: '#d6c4a3', align: 'center',
        shadow: { offsetX: 0, offsetY: 1, color: '#000000', blur: 3, fill: true },
      }).setOrigin(0.5, 0).setDepth(PAUSE_GEAR_HUD.depth + 3)
      .setScrollFactor(0).setVisible(false);
    this.setLabel.setFixedSize?.(PAUSE_GEAR_HUD.setTextMaxW, PAUSE_GEAR_HUD.setTextLineH);
    this.setLabel.setMaxLines?.(1);
  }

  cellX(i) {
    return PAUSE_GEAR_HUD.x + i * (PAUSE_GEAR_HUD.cellW + PAUSE_GEAR_HUD.gap);
  }

  setVisible(visible) {
    this.visible = !!visible;
    this.g.setVisible(this.visible);
    for (const label of this.labels) label.setVisible(this.visible);
    for (const icon of this.icons) icon.setVisible(this.visible && !!icon._gearTextureKey);
    this.setLabel.setVisible(this.visible);
  }

  redraw(entries, setState = this.setState) {
    const H = PAUSE_GEAR_HUD;
    const g = this.g;
    g.clear();
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const x = this.cellX(i);
      g.fillStyle(0x0d0b0a, 0.82);
      g.fillRoundedRect(x, H.y, H.cellW, H.cellH, H.radius);
      g.lineStyle(2, entry.color, 0.92);
      g.strokeRoundedRect(x, H.y, H.cellW, H.cellH, H.radius);
      g.lineStyle(1, 0xf1e6cf, 0.08);
      g.lineBetween(x + 8, H.y + 1, x + H.cellW - 8, H.y + 1);

      const pipWidth = (TIER_MAX - 1) * H.pipGap;
      drawTierDots(g, x + (H.cellW - pipWidth) / 2, H.y + H.pipY, entry.tier, {
        gap: H.pipGap,
        radius: H.pipRadius,
        diamondScale: 0.92,
      });
    }

    if (setState.visible) {
      g.lineStyle(setState.lineWidth, 0xf1e6cf, setState.lineAlpha);
      for (const slot of setState.slots) {
        const i = SLOTS.indexOf(slot);
        if (i < 0) continue;
        const x = this.cellX(i);
        g.lineBetween(x + 8, H.y + H.setLineY,
          x + H.cellW - 8, H.y + H.setLineY);
      }
    }

  }

  sync(gear, visible) {
    if (!visible || !gear) {
      this.setVisible(false);
      this.forceRedraw = true;
      return;
    }

    const signature = pauseGearHudSignature(gear);
    const entries = pauseGearHudEntries(gear);
    const setState = pauseGearHudSetState(gear);
    const gearChanged = signature !== this.signature;
    if (gearChanged || this.forceRedraw) {
      this.signature = signature;
      this.setState = setState;
      this.redraw(entries, setState);
      this.forceRedraw = false;

      const statusText = `현재 세트 · ${setState.text || '세트 후보 없음'}`;
      if (typeof this.setLabel.setText === 'function') this.setLabel.setText(statusText);
      else this.setLabel.text = statusText;

      if (gearChanged) for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const icon = this.icons[i];
        const hasTexture = !!entry.textureKey && this.scene.textures.exists(entry.textureKey);
        if (!hasTexture) {
          icon._gearTextureKey = null;
          icon.setVisible(false);
          continue;
        }
        // 등급만 바뀌면 틀과 점만 다시 그린다. 실제 부품 종류가 바뀔 때만 텍스처를 교체한다.
        if (icon._gearTextureKey !== entry.textureKey) {
          icon.setTexture(entry.textureKey);
          icon._gearTextureKey = entry.textureKey;
        }
        const x = this.cellX(i);
        icon.setDisplaySize(PAUSE_GEAR_HUD.icon, PAUSE_GEAR_HUD.icon)
          .setPosition(x + PAUSE_GEAR_HUD.cellW / 2,
            PAUSE_GEAR_HUD.y + PAUSE_GEAR_HUD.iconTop + PAUSE_GEAR_HUD.icon / 2);
      }
    }
    this.setVisible(true);
  }
}
